import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

const linkClass =
  "text-left hover:text-white/50 text-[12px] leading-5 sm:text-[16px] sm:leading-6 md:text-[18px]";

const FooterSkeleton = () => {
  return (
    <footer className="bg-[#2f4b44] text-white w-full overflow-x-hidden">
      <div className="mx-auto max-w-7xl px-4 py-7 sm:px-6 sm:py-12 md:px-8 md:py-16">
        <div className="grid gap-6 sm:gap-10 md:grid-cols-3 md:gap-12">
          <div>
            <div className="h-7 w-32 rounded bg-gray-600 animate-pulse sm:h-9 md:h-10" />
            <div className="mt-3 space-y-2 sm:mt-6">
              <div className="h-4 w-full rounded bg-gray-600 animate-pulse" />
              <div className="h-4 w-11/12 rounded bg-gray-600 animate-pulse" />
              <div className="h-4 w-10/12 rounded bg-gray-600 animate-pulse" />
            </div>
            <div className="mt-5 h-5 w-40 rounded bg-gray-600 animate-pulse sm:mt-10" />
            <div className="mt-2 flex gap-2 sm:mt-3 sm:gap-3">
              <div className="h-9 flex-1 rounded bg-gray-600 animate-pulse sm:h-10" />
              <div className="h-9 w-24 rounded bg-gray-600 animate-pulse sm:h-10" />
            </div>
          </div>
          <div>
            <div className="h-5 w-32 rounded bg-gray-600 animate-pulse" />
            <div className="mt-3 grid grid-cols-2 gap-x-6 gap-y-1.5 sm:mt-6 sm:gap-x-10 sm:gap-y-2">
              {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                <div key={i} className="h-4 w-24 rounded bg-gray-600 animate-pulse" />
              ))}
            </div>
          </div>
          <div>
            <div className="h-5 w-24 rounded bg-gray-600 animate-pulse" />
            <div className="mt-3 space-y-2 sm:mt-6">
              <div className="h-4 w-20 rounded bg-gray-600 animate-pulse" />
              <div className="h-4 w-32 rounded bg-gray-600 animate-pulse" />
              <div className="h-4 w-20 rounded bg-gray-600 animate-pulse" />
              <div className="h-4 w-48 rounded bg-gray-600 animate-pulse" />
            </div>
            <div className="mt-4 h-8 w-8 rounded-full bg-gray-600 animate-pulse sm:mt-6 sm:h-10 sm:w-10" />
          </div>
        </div>
        <div className="mt-6 h-4 w-64 rounded bg-gray-600 animate-pulse sm:mt-12" />
      </div>
    </footer>
  );
};

export default function Footer({ onNav, onSubscribe, loading = false }) {
  const { t } = useTranslation();
  
  if (loading) return <FooterSkeleton />;
  return (
    <footer className="bg-[#2f4b44] text-white w-full overflow-x-hidden">
      {/* ✅ ลด padding มือถือให้เตี้ยลง */}
      <div className="mx-auto max-w-7xl px-4 py-7 sm:px-6 sm:py-12 md:px-8 md:py-16">
        {/* ✅ ลด gap มือถือ */}
        <div className="grid gap-6 sm:gap-10 md:grid-cols-3 md:gap-12">
          {/* Left */}
          <div>
            <img
              src={`${process.env.PUBLIC_URL}/assets/icons/logo.svg`}
              alt="Supreme Team"
              className="h-7 w-auto object-contain sm:h-9 md:h-10"
            />

            {/* ✅ ลด font/spacing มือถือ */}
            <p className="mt-3 max-w-sm font-poppins text-[12px] leading-5 text-white sm:mt-6 sm:text-[16px] sm:leading-6 md:text-[18px]">
              {t("footer.description")}
            </p>

            <div className="mt-5 font-poppins text-[12px] font-bold text-white sm:mt-10 sm:text-[16px] md:text-[18px]">
              {t("footer.subscribeNewsletter")}
            </div>

            {/* ✅ อินพุต/ปุ่มเตี้ยลงบนมือถือ */}
            <div className="mt-2 flex max-w-md gap-2 sm:mt-3 sm:gap-3">
              <input
                type="email"
                placeholder={t("footer.emailPlaceholder")}
                className="
                  h-9 w-full rounded-md
                  border border-white/30 bg-white/10
                  px-3 text-[11px] text-white
                  placeholder:text-white/50
                  outline-none focus:border-white/60
                  sm:h-10 sm:px-4 sm:text-[13px] md:text-[14px]
                "
              />
              <button
                type="button"
                onClick={onSubscribe}
                className="
                  h-9 shrink-0 rounded-md bg-[#EE3124]
                  px-3 font-poppins font-bold text-white
                  text-[13px]
                  hover:opacity-70
                  sm:h-10 sm:px-5 sm:text-[18px] md:px-6 md:text-[20px]
                "
              >
                {t("footer.subscribe")}
              </button>
            </div>
          </div>

          {/* Middle */}
          <div>
            <div className="font-poppins text-[12px] font-bold uppercase tracking-wider text-white sm:text-[16px] md:text-[18px]">
              {t("footer.quickLinks")}
            </div>

            {/* ✅ ลด margin + ลด font มือถือ + ลด gap */}
            <div className="mt-3 grid grid-cols-2 gap-x-6 gap-y-1.5 font-poppins text-white sm:mt-6 sm:gap-x-10 sm:gap-y-2">
              {[
                ["footer.quickLinkHome", "home", "/"],
                ["footer.quickLinkLiving", "living", "/#living"],
                ["footer.quickLinkHero", "hero", "/live-or-invest"],
                ["footer.quickLinkContact", "contact", "/contact-us"],
                ["footer.quickLinkConcept", "concept", "/read-our-story"],
                ["footer.quickLinkCareer", "career", "/career"],
                ["footer.quickLinkNews", "news", "/learn-more"],
                ["footer.quickLinkAdmin", "admin", null],
              ].map(([labelKey, key, path]) =>
                path ? (
                  <Link
                    key={key}
                    to={path}
                    className={linkClass}
                  >
                    {t(labelKey)}
                  </Link>
                ) : (
                  <button
                    key={key}
                    type="button"
                    onClick={() => onNav?.(key)}
                    className={linkClass}
                  >
                    {t(labelKey)}
                  </button>
                )
              )}
            </div>
          </div>

          {/* Right */}
          <div>
            <div className="font-poppins text-[12px] font-bold uppercase tracking-wider text-white/90 sm:text-[16px] md:text-[18px]">
              {t("footer.contact")}
            </div>

            {/* ✅ ลด spacing มือถือ */}
            <div className="mt-3 text-white/90 sm:mt-6">
              <div className="font-poppins font-bold text-[12px] text-white sm:text-[16px] md:text-[18px]">
                {t("footer.phone")}
              </div>
              <div className="font-poppins text-[12px] text-white sm:text-[16px] md:text-[18px]">
                02 679 8139 - 6
              </div>

              <div className="mt-3 font-poppins font-bold text-[12px] text-white sm:mt-4 sm:text-[16px] md:text-[18px]">
                {t("footer.email")}
              </div>
              <div className="font-poppins text-[12px] text-white sm:text-[16px] md:text-[18px]">
                {t("footer.contactEmail")}
              </div>
            </div>

            <div className="mt-4 sm:mt-6">
              <button
                type="button"
                className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-[#B2553D] text-white hover:opacity-90 sm:h-10 sm:w-10"
                aria-label="Social"
              >
                f
              </button>
            </div>
          </div>
        </div>

        {/* ✅ ลดระยะห่าง/ขนาด copyright มือถือ */}
        <div className="mt-6 border-t border-white/25 pt-4 font-poppins text-white text-[11px] sm:mt-12 sm:pt-6 sm:text-[13px] md:text-[14px]">
          {t("footer.copyright")}
        </div>
      </div>
    </footer>
  );
}
